const ACCESS_TOKEN = '37e8894892fa7b357eae0413ef9261fc'

export interface StorefrontProduct {
  id: string
  title: string
  onlineStoreUrl: string
  trackingParameters: string | null
  availableForSale: boolean
  compatible_dispenser: { value: string } | null
  dispenser_type: { value: string } | null
  fragrance_name: { value: string } | null
  fragrance_group_id: { value: string } | null
  onboarding_usage_settings: { value: string } | null
  onboarding_usage_metric: { value: string } | null
  images: Images
  description: string
  descriptionHtml: string
  sellingPlanGroups: SellingPlanGroups
  variants: Variants
}

export interface Images {
  edges: ImageEdge[]
}

export interface ImageEdge {
  node: Image
}

export interface Image {
  altText: string
  small: string
  medium: string
  large: string
  original: string
  height: number
  width: number
}

export interface SellingPlanGroups {
  edges: SellingPlanGroupEdge[]
}

export interface SellingPlanGroupEdge {
  node: SellingPlanGroup
}

export interface SellingPlanGroup {
  name: string
  sellingPlans: SellingPlans
}

export interface SellingPlans {
  edges: SellingPlanEdge[]
}

export interface SellingPlanEdge {
  node: SellingPlan
}

export interface SellingPlan {
  id: string
  name: string
  priceAdjustments: PriceAdjustment[]
}

export interface PriceAdjustment {
  adjustmentValue: AdjustmentValue
}

export interface AdjustmentValue {
  adjustmentPercentage: number
}

export interface Variants {
  edges: VariantEdge[]
}

export interface VariantEdge {
  node: Variant
}

export interface Variant {
  id: string
  title: string
  image: Image
  price: Price
  sku: string
  availableForSale: boolean
}

export interface Image {
  altText: string
  small: string
  medium: string
  large: string
  original: string
}

export interface Price {
  amount: string
}

export async function loadProduct(
  id: number | string,
): Promise<StorefrontProduct> {
  const res = await fetch(
    `https://bower-collective.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query Products($id: ID!) {
          node(id: $id) {
            ... on Product {
              ${productFragment}
            }
          }
        }`,
        variables: {
          id: `gid://shopify/Product/${id}`,
        },
      }),
    },
  )
  if (res.ok) {
    const resJson = await res.json()
    return resJson.data.node as StorefrontProduct
  } else {
    throw new Error('Cannot load product')
  }
}

export async function loadDispensers(): Promise<StorefrontProduct[]> {
  const res = await fetch(
    `https://bower-collective.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query Dispensers {
          collection(handle: "dispensers") {
            products(first: 15, filters: { available: true }) {
              edges {
                node {
                  ${productFragment}
                }
              }
            }
          }
        }`,
      }),
    },
  )
  if (res.ok) {
    const resJson = await res.json()
    return resJson.data.collection.products.edges.map(
      (e: { node: StorefrontProduct }) => e.node,
    )
  } else {
    throw new Error('Cannot load product')
  }
}

export async function loadAlternateFragrances(
  fragranceGroupId: string,
): Promise<StorefrontProduct[]> {
  const res = await fetch(
    `https://bower-collective.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query FragranceOptions($fragranceGroupId: String!) {
          collection(handle: "all") {
            products(first: 15, filters: {
              productMetafield: {
                namespace: "product_meta",
                key: "fragrance_group_id",
                value: $fragranceGroupId
              }
            }) {
              edges {
                node {
                  ${productFragment}
                }
              }
            }
          }
        }`,
        variables: {
          fragranceGroupId,
        },
      }),
    },
  )
  if (res.ok) {
    const resJson = await res.json()
    return resJson.data.collection.products.edges.map(
      (e: { node: StorefrontProduct }) => e.node,
    )
  } else {
    throw new Error('Cannot load product')
  }
}

const productFragment = `
id
title
availableForSale
description
descriptionHtml
onlineStoreUrl
trackingParameters
compatible_dispenser: metafield(namespace: "collection_filter", key: "compatible_dispenser") {
  value
}
dispenser_type: metafield(namespace: "collection_filter", key: "fil_dispenser_type") {
  value
}
fragrance_name: metafield(namespace: "product_meta", key: "fragrance_name") {
  value
}
fragrance_group_id: metafield(namespace: "product_meta", key: "fragrance_group_id") {
  value
}
onboarding_usage_settings: metafield(namespace: "product_meta", key: "onboarding_usage_settings") {
  value
}
onboarding_usage_metric: metafield(namespace: "product_meta", key: "onboarding_usage_metric") {
  value
}
images(first: 1) {
  edges {
    node {
      altText
      small: url(transform: { maxWidth: 200 })
      medium: url(transform: { maxWidth: 500 })
      large: url(transform: { maxWidth: 1000 })
      original: url
      height
      width
    }
  }
}
sellingPlanGroups(first: 2) {
  edges {
    node {
      name
      sellingPlans(first: 10) {
        edges {
          node {
            id
            name
            priceAdjustments {
              adjustmentValue {
                ... on SellingPlanPercentagePriceAdjustment {
                  adjustmentPercentage
                }
              }
            }
          }
        }

      }
    }
  }
}
variants(first: 10) {
  edges {
    node {
      id
      availableForSale
      title
      sku
      image {
        altText
        small: url(transform: { maxWidth: 200 })
        medium: url(transform: { maxWidth: 500 })
        large: url(transform: { maxWidth: 1000 })
        height
        width
        original: url
      }
      price {
        amount
      }
    }
  }
}`

export interface PredictiveSearchResults {
  data: {
    predictiveSearch: {
      queries: { text: string; trackingParameters: string | null }[]
      products: StorefrontProduct[]
      articles: {
        title: string
        onlineStoreUrl: string
        trackingParameters: string | null
        image: { height: number; width: number; url: string }
        excerpt: string
        seo: {
          description: string
        }
      }[]
      pages: {
        title: string
        onlineStoreUrl: string
        trackingParameters: string | null
        bodySummary: string
        seo: {
          description: string
        }
      }[]
    }
  }
}
export async function loadPredictiveSearchResults(
  query: string,
): Promise<PredictiveSearchResults> {
  const res = await fetch(
    `https://bower-collective.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query suggestions($query: String!) {
            predictiveSearch(query: $query, limit: 10) {
              queries {
                text
                trackingParameters
              }
              products {
                ${productFragment}
              }
              pages {
                id
                title
                onlineStoreUrl
                trackingParameters
                bodySummary
                seo {
                  description
                }
              }
              articles {
                id
                title
                onlineStoreUrl
                trackingParameters
                excerpt
                seo {
                  description
                }
                image {
                  url
                  width
                  height
                }
              }
            }
          }
        `,
        variables: {
          query,
        },
      }),
    },
  )
  return await res.json()
}

export async function loadVariant(id: string): Promise<StorefrontProduct> {
  const res = await fetch(
    `https://bower-collective.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query Variant($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              product {
                ${productFragment}
              }
            }
          }
        }`,
        variables: {
          id: `gid://shopify/ProductVariant/${id}`,
        },
      }),
    },
  )
  if (res.ok) {
    const resJson = await res.json()
    return resJson.data.node.product as StorefrontProduct
  } else {
    throw new Error('Cannot load variant')
  }
}
